import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useSpecialistAuth } from '../../context/SpecialistAuthContext';
import { API_URL } from '../../config/api';
import SpecialistSidebar from '../SpecialistSidebar';
// Reuse same CSS as SellerDashboardLayout for identical look
import '../SellerDashboardLayout/SellerDashboardLayout.css';

const SpecialistDashboardLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { specialist, logout, isAuthenticated } = useSpecialistAuth();
  const [showMobileProfileDropdown, setShowMobileProfileDropdown] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Fix mobile viewport height (for iOS Safari address bar issue)
  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };
    setVH();
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', setVH);
    return () => {
      window.removeEventListener('resize', setVH);
      window.removeEventListener('orientationchange', setVH);
    };
  }, []);

  // Close mobile sidebar on route change
  useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile sidebar is open
  useEffect(() => {
    if (isMobileSidebarOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [isMobileSidebarOpen]);

  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };

  const sidebarMenuItems = [
    { id: 'dashboard', icon: 'home', label: 'Dashboard', path: '/specialist-dashboard' },
    { id: 'projects', icon: 'list', label: 'Project Lists', path: '/specialist-projects' },
    { id: 'rfq', icon: 'bell', label: 'RFQ Manage', path: '/specialist-rfq' },
    { id: 'messages', icon: 'chat', label: 'Inqs / Msgs', path: '/specialist-messages' },
    { id: 'membership', icon: 'invoice', label: 'Membership Plan', path: '/specialist-membership' },
    { id: 'support', icon: 'chat', label: 'Support', path: '/specialist-support' },
    { id: 'profile', icon: 'user', label: 'Specialist Profile', path: '/specialist-profile' },
  ];

  const isActivePath = (itemPath, itemId) => {
    if (itemId === 'dashboard' && location.pathname === '/specialist-dashboard') return true;
    if (itemId === 'projects' && location.pathname.startsWith('/specialist-projects')) return true;
    if (itemId === 'rfq' && location.pathname.startsWith('/specialist-rfq')) return true;
    if (itemId === 'messages' && location.pathname.startsWith('/specialist-messages')) return true;
    if (itemId === 'membership' && location.pathname.startsWith('/specialist-membership')) return true;
    if (itemId === 'support' && location.pathname.startsWith('/specialist-support')) return true;
    if (itemId === 'profile' && location.pathname.startsWith('/specialist-profile')) return true;
    return location.pathname === itemPath;
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const specialistName = specialist?.name || specialist?.companyName || 'Specialist';

  return (
    <div className="seller-dashboard-layout">
      {/* Header - identical to supplier */}
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            {/* Mobile Hamburger Button */}
            <button className="mobile-hamburger-btn mobile-only" onClick={toggleMobileSidebar} aria-label="Menu">
              {isMobileSidebarOpen ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : (
                <img src="/mobile-icons/hamburger-menu.png" alt="Menu" className="hamburger-icon-img" />
              )}
            </button>

            <img 
              src="/logo.png?v=2" 
              alt="Conflux" 
              className="logo clickable-logo"
              onClick={handleLogoClick}
              title="Go to Home"
            />
          </div>
          
          {/* Desktop Header Right */}
          <div className="header-right desktop-only">
            <div className="language-selector">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9.58333 2.15234C7.93516 2.15234 6.32399 2.64109 4.95358 3.55676C3.58317 4.47244 2.51507 5.77393 1.88434 7.29665C1.25361 8.81936 1.08858 10.4949 1.41013 12.1114C1.73167 13.7279 2.52534 15.2128 3.69078 16.3782C4.85622 17.5437 6.34107 18.3373 7.95758 18.6589C9.57409 18.9804 11.2496 18.8154 12.7724 18.1847C14.2951 17.5539 15.5966 16.4858 16.5122 15.1154C17.4279 13.745 17.9167 12.1339 17.9167 10.4857C17.9141 8.27632 17.0353 6.15818 15.4731 4.59593C13.9108 3.03368 11.7927 2.15489 9.58333 2.15234Z" stroke="#354556" strokeWidth="1.25"/>
              </svg>
              <span>عربي</span>
            </div>
            <div className="user-profile">
              <div className="user-avatar">
                {specialist?.avatar ? (
                  <img
                    src={specialist.avatar.startsWith('http') ? specialist.avatar : `${API_URL.replace('/api', '')}${specialist.avatar}`}
                    alt="Profile"
                    style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'block'); }}
                  />
                ) : (
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="20" cy="20" r="20" fill="rgba(0,0,0,0.16)"/>
                    <circle cx="20" cy="16" r="6" fill="white"/>
                    <path d="M10 32C10 27.5817 14.4772 24 20 24C25.5228 24 30 27.5817 30 32" fill="white"/>
                  </svg>
                )}
              </div>
              <div className="user-info">
                <span className="company-name-header">{specialistName}</span>
                <div className="user-role">
                  <span>Specialist</span>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 6.33398L8 10.334L4 6.33398H12Z" fill="#6D6D6D" stroke="#6D6D6D" strokeWidth="2" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>
          
          {/* Mobile Profile Button */}
          <div className="mobile-profile-wrapper mobile-only">
            <button 
              className="mobile-profile-btn" 
              onClick={() => setShowMobileProfileDropdown(!showMobileProfileDropdown)}
              aria-label="Profile Menu"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            
            {showMobileProfileDropdown && (
              <>
                <div className="mobile-profile-overlay" onClick={() => setShowMobileProfileDropdown(false)}></div>
                <div className="mobile-profile-dropdown">
                  <div className="mobile-profile-menu">
                    <button className="mobile-profile-item" onClick={() => { setShowMobileProfileDropdown(false); navigate('/specialist-messages'); }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M22 6L12 13L2 6" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Messages</span>
                    </button>
                    
                    <button className="mobile-profile-item" onClick={() => { setShowMobileProfileDropdown(false); navigate('/specialist-rfq'); }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M14 2V8H20" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M16 13H8M16 17H8M10 9H8" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>RFQ</span>
                    </button>
                    
                    <button className="mobile-profile-item" onClick={() => { setShowMobileProfileDropdown(false); navigate('/specialist-projects'); }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M4 6.25H20M4 12H15M4 17H9" stroke="#333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Projects</span>
                    </button>
                    
                    <button className="mobile-profile-logout" onClick={() => { setShowMobileProfileDropdown(false); handleLogout(); }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M16 17L21 12L16 7" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M21 12H9" stroke="#dc3545" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Logout
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      <div 
        className={`mobile-sidebar-overlay ${isMobileSidebarOpen ? 'active' : ''}`} 
        onClick={toggleMobileSidebar}
      />

      {/* Mobile Sidebar Drawer */}
      <nav className={`mobile-sidebar-drawer ${isMobileSidebarOpen ? 'open' : ''}`}>
        <div className="mobile-drawer-header">
          <img 
            src="/logo.png?v=2" 
            alt="Conflux" 
            className="drawer-logo"
            onClick={() => { setIsMobileSidebarOpen(false); navigate('/'); }}
          />
          <button className="drawer-close-btn" onClick={toggleMobileSidebar}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {isAuthenticated && (
          <div className="mobile-drawer-user">
            <div className="drawer-user-avatar">
              {specialist?.avatar ? (
                <img
                  src={specialist.avatar.startsWith('http') ? specialist.avatar : `${API_URL.replace('/api', '')}${specialist.avatar}`}
                  alt="Profile"
                  style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                  <circle cx="20" cy="20" r="20" fill="#1F396C"/>
                  <circle cx="20" cy="16" r="6" fill="white"/>
                  <path d="M10 32C10 27.5817 14.4772 24 20 24C25.5228 24 30 27.5817 30 32" fill="white"/>
                </svg>
              )}
            </div>
            <div className="drawer-user-info">
              <span className="drawer-company-name">{specialistName}</span>
              <span className="drawer-user-role">Specialist</span>
            </div>
          </div>
        )}

        <div className="mobile-drawer-nav">
          {sidebarMenuItems.map((item) => (
            <Link
              key={item.id}
              to={item.path}
              className={`mobile-drawer-item ${isActivePath(item.path, item.id) ? 'active' : ''}`}
              onClick={() => setIsMobileSidebarOpen(false)}
            >
              <span className="drawer-item-icon">
                {item.id === 'dashboard' && (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M9 22V12H15V22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                )}
                {item.id === 'projects' && (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M4 6.25H20M4 12H15M4 17H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                )}
                {item.id === 'rfq' && (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                )}
                {item.id === 'messages' && (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                )}
                {item.id === 'membership' && (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M14 2V8H20M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                )}
                {item.id === 'profile' && (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                )}
              </span>
              <span className="drawer-item-label">{item.label}</span>
            </Link>
          ))}
        </div>

        <div className="mobile-drawer-footer">
          <button className="mobile-drawer-logout" onClick={() => { setIsMobileSidebarOpen(false); handleLogout(); }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="#dc3545" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 17L21 12L16 7" stroke="#dc3545" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M21 12H9" stroke="#dc3545" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Logout</span>
          </button>
        </div>
      </nav>

      <div className="dashboard-body">
        <SpecialistSidebar />
        <main className="dashboard-main-content">
          {children}
        </main>
      </div>
    </div>
  );
};

export default SpecialistDashboardLayout;
